// Booking strategy registry.
//
// Each strategy exports { buildBookingUrl(ctx), describeBookingInputs(ctx) }
// and is keyed by the Agency.scanner enum value (see ScannerType in
// prisma/schema.prisma).
//
// This module MUST be server-safe: no React, no Chakra, no hooks. It is
// consumed by both app/components/AvailabilityMatchDetails.jsx (browser +
// server render) and the V1 public API routes (server only).
//
// Keys must match the ScannerType enum exactly. Unknown scanners fall through
// to defaultStrategy (passthrough of site.reserveUrl).

import acc from "./acc.js";
import accwhistler from "./accwhistler.js";
import aspira from "./aspira.js";
import arizonastateparks from "./arizonastateparks.js";
import ohiostateparks from "./ohiostateparks.js";
import virginiastateparks from "./virginiastateparks.js";
import minnesotastateparks from "./minnesotastateparks.js";
import northdakotastateparks from "./northdakotastateparks.js";
import nevadastateparks from "./nevadastateparks.js";
import illinoisstateparks from "./illinoisstateparks.js";
import aucklandcouncil from "./aucklandcouncil.js";
import camis from "./camis.js";
import campspot from "./campspot.js";
import defaultStrategy from "./default.js";
import gorcapa from "./gorcapa.js";
import hampshireholidayparks from "./hampshireholidayparks.js";
import mountkiddrv from "./mountkiddrv.js";
import melanzana from "./melanzana.js";
import nswparks from "./nswparks.js";
import ningaloocoralbay from "./ningaloocoralbay.js";
import nzdoc from "./nzdoc.js";
import nrmaparksresorts from "./nrmaparksresorts.js";
import ingeniaholidays from "./ingeniaholidays.js";
import nztop10 from "./nztop10.js";
import rac from "./rac.js";
import stayrottnest from "./stayrottnest.js";
import spearheadhuts from "./spearheadhuts.js";
import waparks from "./waparks.js";
import webrezpro from "./webrezpro.js";
import wyomingparks from "./wyomingparks.js";
import xanterra from "./xanterra.js";
import tasmanholidayparks from "./tasmanholidayparks.js";
import { newbookTenants } from "./newbook.js";
import hawaiistateparks from "./hawaiistateparks.js";
import recdotgov from "./recdotgov.js";
import timbertraillodge from "./timbertraillodge.js";
import spacetoco from "./spacetoco.js";
import reflectionsholidayparks from "./reflectionsholidayparks.js";
import shireofcoorow from "./shireofcoorow.js";
import summerstartouristparks from "./summerstartouristparks.js";
import big4holidayparks from "./big4holidayparks.js";
import elquestro from "./elquestro.js";
import gdayparks from "./gdayparks.js";
import discoveryparks from "./discoveryparks.js";

// Keys are Agency.scanner enum values. The registry is AUTHORITATIVE — every
// ScannerType enum value in prisma/schema.prisma MUST be present. Adding a
// new scanner without registering it here will throw at resolveStrategy()
// time, caught by the route's top-level try/catch as a 500 INTERNAL_ERROR.
//
// If a scanner has no custom URL logic, point it at `defaultStrategy`
// explicitly rather than relying on a fallback.
const registry = {
  // --- CAMIS family ------------------------------------------------------
  // CAMIS scanners share buildCamisReserveUrl. The original
  // AvailabilityMatchButton used `base:` as the implicit fallthrough for all
  // CAMIS scanners; here we enumerate them explicitly so that adding a new
  // non-CAMIS scanner won't silently get CAMIS URL-building.
  ParksCanada: camis,
  BCParks: camis,
  OntarioParks: camis,
  MBParks: camis,
  NSParks: camis,
  NBParks: camis,
  NLParks: camis,
  YTParks: camis,
  NWTParks: camis,
  LongPointRegion: camis,
  AlgonquinHighlands: camis,
  MaitlandValley: camis,
  NUParks: camis,
  QCParks: camis,
  PAParks: camis,
  TestAgency: camis,
  GatineauPark: camis,
  UpperThames: camis,
  StClairRegion: camis,
  WashingtonParks: camis,
  MichiganStateParks: camis,
  WisconsinStateParks: camis,

  // --- Direct Newbook Cloud tenants ---------------------------------------
  // One strategy, parameterized per tenant (./newbook.js).
  YardieHomestead: newbookTenants.YardieHomestead,
  PeoplesParkCoralBay: newbookTenants.PeoplesParkCoralBay,
  NingalooCaravanHolidayResort: newbookTenants.NingalooCaravanHolidayResort,
  BullaraStation: newbookTenants.BullaraStation,
  BarnHillStation: newbookTenants.BarnHillStation,
  KalbarriAnchorage: newbookTenants.KalbarriAnchorage,
  BathersParadise: newbookTenants.BathersParadise,
  EsperancePinkLake: newbookTenants.EsperancePinkLake,
  SunshineCoastHolidayParks: newbookTenants.SunshineCoastHolidayParks,

  // --- Aspira family -----------------------------------------------------
  AlbertaParks: aspira,
  SKParks: aspira,
  PEIParks: aspira,

  // --- Mews --------------------------------------------------------------
  ACC: acc,

  // --- Custom per-agency strategies -------------------------------------
  NZDOC: nzdoc,
  ArizonaStateParks: arizonastateparks,
  OhioStateParks: ohiostateparks,
  VirginiaStateParks: virginiastateparks,
  MinnesotaStateParks: minnesotastateparks,
  NorthDakotaStateParks: northdakotastateparks,
  NevadaStateParks: nevadastateparks,
  MissouriStateParks: defaultStrategy,
  IllinoisStateParks: illinoisstateparks,
  NZTop10: nztop10,
  Campspot: campspot,
  NSWParks: nswparks,
  NingalooCoralBay: ningaloocoralbay,
  WAParks: waparks,
  StayRottnest: stayrottnest,
  WebRezPro: webrezpro,
  RACParksResorts: rac,
  NRMAParksResorts: nrmaparksresorts,
  IngeniaHolidays: ingeniaholidays,
  HawaiiStateParks: hawaiistateparks,
  AucklandCouncil: aucklandcouncil,
  TasmanHolidayParksAU: tasmanholidayparks,
  TasmanHolidayParksNZ: tasmanholidayparks,
  HampshireHolidayParks: hampshireholidayparks,
  SpearheadHuts: spearheadhuts,
  ACCWhistler: accwhistler,
  WyomingParks: wyomingparks,
  Melanzana: melanzana,
  RecDotGov: recdotgov,

  // --- Xanterra family ---------------------------------------------------
  YellowstoneLodges: xanterra,

  // --- Mount Kidd: same booking URL as default but custom preview ------
  MountKiddRV: mountkiddrv,

  // --- reserveUrl passthrough (default behavior) -------------------------
  // Listed explicitly so we get named-strategy clarity; behavior matches
  // defaultStrategy but the intent is documented per-agency.
  ACCVancouverIsland: defaultStrategy,
  FloridaParks: defaultStrategy,
  ReserveCalifornia: defaultStrategy,
  BCParksDayUse: defaultStrategy,
  ParksVictoria: defaultStrategy,
  GORCAPA: gorcapa,
  TimberTrailLodge: timbertraillodge,
  SpaceToCo: spacetoco,
  ReflectionsHolidayParks: reflectionsholidayparks,
  ShireOfCoorow: shireofcoorow,
  SummerstarTouristParks: summerstartouristparks,
  BIG4HolidayParks: big4holidayparks,
  ElQuestro: elquestro,
  GdayParks: gdayparks,
  DiscoveryParks: discoveryparks,
  QueenslandParks: defaultStrategy,
  ThorsborneTrail: defaultStrategy,
  TASParks: defaultStrategy,
  NTParks: defaultStrategy,
  ACTParks: defaultStrategy,
  SAParks: defaultStrategy,
};

/**
 * Resolve the booking strategy for a given agency scanner.
 *
 * Null / undefined scanners fall through to the default strategy — this
 * covers the catalog-less case (agency row without a scanner assigned, or
 * callers that don't carry agency context yet). Anything else MUST be
 * registered above; an unregistered string throws a clear error so that
 * adding a new ScannerType without updating this file fails loudly
 * instead of quietly falling back to passthrough URL behavior.
 */
export function resolveStrategy(agencyScanner) {
  if (!agencyScanner) return defaultStrategy;
  const strategy = registry[agencyScanner];
  if (!strategy) {
    throw new Error(
      `Unknown scanner: ${agencyScanner}. Register it in app/booking/strategies/index.js.`,
    );
  }
  return strategy;
}

export { defaultStrategy };
